import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify';

import getErrorMessage from '../utils/error';
import { ITestimonialStore } from '../types/types';
import { createTestimonial, deleteTestimonial, getTestimonials, updateTestimonial } from '../utils/api';

const useTestimonial: UseBoundStore<StoreApi<ITestimonialStore>> = create((set) => ({
  testimonials: [],
  saveLoading: false,
  fetchLoading: false,
  removeLoading: false,
  hasFetched: false,

  fetchTestimonials: async () => {
    try {
      set({ fetchLoading: true });
      const { success, message, data } = await getTestimonials();
      if (!success) throw new Error(message);
      set({
        testimonials: data.sort((a: any, b: any) => new Date(b.edited_at).getTime() - new Date(a.edited_at).getTime()),
        hasFetched: true,
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ fetchLoading: false });
    }
  },

  addTestimonial: async (testimonialData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await createTestimonial(testimonialData);
      if (!success) throw new Error(message);
      set((state) => ({ testimonials: state.testimonials ? [...state.testimonials, data] : [data] }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  editTestimonial: async (id, testimonialData) => {
    try {
      set({ saveLoading: true });
      const { success, message, data } = await updateTestimonial(id, testimonialData);
      if (!success) throw new Error(message);
      set((state) => ({
        testimonials: state.testimonials?.map((testimonial) => (testimonial.testimoni_id === id ? data : testimonial)),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ saveLoading: false });
    }
  },

  removeTestimonial: async (id) => {
    try {
      set({ removeLoading: true });
      const { success, message } = await deleteTestimonial(id);
      if (!success) throw new Error(message);
      set((state) => ({
        testimonials: state.testimonials?.filter((testimonial) => testimonial.testimoni_id !== id),
      }));
      toast.success(message, {
        position: 'top-right',
      });
      return { message, success };
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message, { position: 'top-right' });
      return { success: false, message };
    } finally {
      set({ removeLoading: false });
    }
  },
}));

export default useTestimonial;
